import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  PmsFolioDto,
  PmsFolioOperationRecordDto,
  PmsPaymentMethodMappingDto,
  PmsPaymentMethodOptionDto,
  PmsPosGuestSearchResultDto,
  PmsPosOperationResponseDto,
  PmsPropertyDto,
  PmsProviderInfoDto,
  PmsSyncJobDto,
  WebhookSubscriptionStatusDto,
} from "../types/pms.types";

export class PmsService extends BaseService {
  async getProviders(): Promise<ApiResult<PmsProviderInfoDto[]>> {
    return this.handleRequest<PmsProviderInfoDto[]>(
      this.client.get("/api/admin/pms/providers"),
    );
  }

  async getApaleoConnectUrl(payload?: {
    flow?: string;
    postAuthRedirectUrl?: string;
  }): Promise<ApiResult<{ redirectUrl: string }>> {
    return this.handleRequest<{ redirectUrl: string }>(
      this.client.post("/api/admin/pms/apaleo/connect", payload ?? {}),
    );
  }

  async listProperties(): Promise<ApiResult<PmsPropertyDto[]>> {
    return this.handleRequest<PmsPropertyDto[]>(
      this.client.get("/api/admin/pms/properties"),
    );
  }

  async getPmsStatus(): Promise<
    ApiResult<import("../types/pms.types").PmsStatusDto>
  > {
    return this.handleRequest<import("../types/pms.types").PmsStatusDto>(
      this.client.get("/api/admin/pms/status"),
    );
  }

  async linkProperty(
    propertyExternalId: string,
    propertyName: string,
  ): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.post("/api/admin/pms/property-link", {
        provider: "apaleo",
        propertyExternalId,
        propertyName,
      }),
    );
  }

  async importRooms(): Promise<ApiResult<PmsSyncJobDto>> {
    return this.handleRequest<PmsSyncJobDto>(
      this.client.post("/api/admin/pms/rooms/import", {}),
    );
  }

  async listRecentRoomSyncs(): Promise<ApiResult<PmsSyncJobDto[]>> {
    return this.handleRequest<PmsSyncJobDto[]>(
      this.client.get("/api/admin/pms/sync-jobs", {
        params: { type: "rooms" },
      }),
    );
  }

  async listTodayCheckins(): Promise<
    ApiResult<import("../../../models").ActiveQrTokenDto[]>
  > {
    return this.handleRequest<import("../../../models").ActiveQrTokenDto[]>(
      this.client.get("/api/admin/pms/checkins/today"),
    );
  }

  async listCheckins(params: {
    page?: number;
    pageSize?: number;
    dateFromUtc?: string;
    dateToUtc?: string;
    status?: string;
    roomNumber?: string;
    guest?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<
    ApiResult<import("../types/checkins.types").CheckinListItemPage>
  > {
    return this.handleRequest<
      import("../types/checkins.types").CheckinListItemPage
    >(this.client.get("/api/admin/pms/checkins", { params }));
  }

  async getPostingSettings(): Promise<
    ApiResult<import("../types/pmsPosting.types").PmsPostingSettingsDto>
  > {
    return this.handleRequest<
      import("../types/pmsPosting.types").PmsPostingSettingsDto
    >(this.client.get("/api/admin/pms/posting/settings"));
  }

  async listIncomingReservations(params: {
    page?: number;
    pageSize?: number;
    dateFromUtc?: string;
    dateToUtc?: string;
    status?: string;
    roomNumber?: string;
    guest?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<
    ApiResult<
      import("../types/incomingReservations.types").IncomingReservationListPage
    >
  > {
    return this.handleRequest<
      import("../types/incomingReservations.types").IncomingReservationListPage
    >(this.client.get("/api/admin/pms/incoming-reservations", { params }));
  }

  async syncIncomingReservations(
    windowDays?: number,
  ): Promise<ApiResult<import("../types/pms.types").PmsSyncJobDto>> {
    return this.handleRequest<import("../types/pms.types").PmsSyncJobDto>(
      this.client.post(
        "/api/admin/pms/incoming-reservations/sync",
        {},
        { params: windowDays ? { windowDays } : undefined },
      ),
    );
  }

  async postOrderToPms(
    orderId: string,
  ): Promise<
    ApiResult<{
      message: string;
      orderId: string;
      status?: string;
      lastError?: string;
      chargeExternalId?: string;
    }>
  > {
    return this.handleRequest<{
      message: string;
      orderId: string;
      status?: string;
      lastError?: string;
      chargeExternalId?: string;
    }>(this.client.post(`/api/admin/pms/orders/${orderId}/post`, {}));
  }

  async getServiceRequestPostingSettings(): Promise<
    ApiResult<
      import("../types/pmsPosting.types").PmsServiceRequestPostingSettingsDto
    >
  > {
    return this.handleRequest<
      import("../types/pmsPosting.types").PmsServiceRequestPostingSettingsDto
    >(this.client.get("/api/admin/pms/posting/service-requests/settings"));
  }

  async getTransferPostingSettings(): Promise<
    ApiResult<import("../types/pmsPosting.types").PmsTransferPostingSettingsDto>
  > {
    return this.handleRequest<
      import("../types/pmsPosting.types").PmsTransferPostingSettingsDto
    >(this.client.get("/api/admin/pms/posting/transfers/settings"));
  }

  async updateTransferPostingSettings(payload: {
    enabled?: boolean; // Legacy
    postPrepaid: boolean;
    postRoomBill: boolean;
    chargeKind?: import("../types/pmsPosting.types").PmsChargeKind;
    folioTargetType?: import("../types/pmsPosting.types").PmsFolioTargetType;
    folioCreationPolicy?: import("../types/pmsPosting.types").PmsFolioCreationPolicy;
    serviceType?: string;
    vatType?: string;
    subAccountId?: string;
    outletKey?: string;
  }): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.put("/api/admin/pms/posting/transfers/settings", payload),
    );
  }

  async getLateCheckoutPostingSettings(): Promise<
    ApiResult<
      import("../types/pmsPosting.types").PmsLateCheckoutPostingSettingsDto
    >
  > {
    return this.handleRequest<
      import("../types/pmsPosting.types").PmsLateCheckoutPostingSettingsDto
    >(this.client.get("/api/admin/pms/posting/late-checkout/settings"));
  }

  async updateLateCheckoutPostingSettings(payload: {
    postPrepaid: boolean;
    postRoomBill: boolean;
    chargeKind?: import("../types/pmsPosting.types").PmsChargeKind;
    folioTargetType?: import("../types/pmsPosting.types").PmsFolioTargetType;
    folioCreationPolicy?: import("../types/pmsPosting.types").PmsFolioCreationPolicy;
    serviceType?: string;
    vatType?: string;
    subAccountId?: string;
    outletKey?: string;
  }): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.put("/api/admin/pms/posting/late-checkout/settings", payload),
    );
  }

  async updateServiceRequestPostingSettings(payload: {
    postPrepaid: boolean;
    postRoomBill: boolean;
    chargeKind?: import("../types/pmsPosting.types").PmsChargeKind;
    folioTargetType?: import("../types/pmsPosting.types").PmsFolioTargetType;
    folioCreationPolicy?: import("../types/pmsPosting.types").PmsFolioCreationPolicy;
    serviceType?: string;
    vatType?: string;
    subAccountId?: string;
    outletKey?: string;
    items: import("../types/pmsPosting.types").PmsServiceRequestPostingTypeDto[];
  }): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.put(
        "/api/admin/pms/posting/service-requests/settings",
        payload,
      ),
    );
  }

  async postServiceRequestToPms(
    serviceRequestId: string,
    amount?: number,
  ): Promise<
    ApiResult<{
      message: string;
      serviceRequestId: string;
      status?: string;
      lastError?: string;
      chargeExternalId?: string;
    }>
  > {
    return this.handleRequest<{
      message: string;
      serviceRequestId: string;
      status?: string;
      lastError?: string;
      chargeExternalId?: string;
    }>(
      this.client.post(
        `/api/admin/pms/service-requests/${serviceRequestId}/post`,
        amount !== undefined ? { amount } : {},
      ),
    );
  }

  async postTransferToPms(
    transferRequestId: string,
  ): Promise<
    ApiResult<{
      message: string;
      transferRequestId: string;
      status?: string;
      lastError?: string;
      chargeExternalId?: string;
    }>
  > {
    return this.handleRequest<{
      message: string;
      transferRequestId: string;
      status?: string;
      lastError?: string;
      chargeExternalId?: string;
    }>(
      this.client.post(
        `/api/admin/pms/transfers/${transferRequestId}/post`,
        {},
      ),
    );
  }

  async listOutbox(params: {
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<
    ApiResult<
      import("../types/pms.types").PaginatedResponse<
        import("../types/pms.types").PmsOutboundMessageDto
      >
    >
  > {
    return this.handleRequest<
      import("../types/pms.types").PaginatedResponse<
        import("../types/pms.types").PmsOutboundMessageDto
      >
    >(this.client.get("/api/admin/pms/outbox", { params }));
  }

  async retryOutbox(id: string): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.post(`/api/admin/pms/outbox/${id}/retry`, {}),
    );
  }

  async deadLetterOutbox(
    id: string,
    reason?: string,
  ): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.post(`/api/admin/pms/outbox/${id}/dead-letter`, { reason }),
    );
  }

  async updatePostingSettings(payload: {
    onlineMode?: import("../types/pmsPosting.types").PmsPostingMode; // Legacy
    offlineMode?: import("../types/pmsPosting.types").PmsPostingMode; // Legacy
    postPrepaid: boolean;
    postRoomBill: boolean;
    chargeKind?: import("../types/pmsPosting.types").PmsChargeKind;
    folioTargetType?: import("../types/pmsPosting.types").PmsFolioTargetType;
    folioCreationPolicy?: import("../types/pmsPosting.types").PmsFolioCreationPolicy;
    serviceType?: string;
    vatType?: string;
    subAccountId?: string;
    outletKey?: string;
  }): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.put("/api/admin/pms/posting/settings", payload),
    );
  }

  async getWebhookStatus(): Promise<ApiResult<WebhookSubscriptionStatusDto>> {
    return this.handleRequest<WebhookSubscriptionStatusDto>(
      this.client.get("/api/admin/pms/webhook-status"),
    );
  }

  async setupWebhook(): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.post("/api/admin/pms/webhook-setup", {}),
    );
  }

  async disconnectApaleo(): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.delete("/api/admin/pms/apaleo/disconnect"),
    );
  }

  async changeApaleoProperty(
    propertyExternalId: string,
    propertyName: string,
  ): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.post("/api/admin/pms/apaleo/change-property", {
        propertyExternalId,
        propertyName,
      }),
    );
  }

  async searchPosGuests(query: string): Promise<ApiResult<PmsPosGuestSearchResultDto[]>> {
    return this.handleRequest<PmsPosGuestSearchResultDto[]>(
      this.client.get("/api/admin/pms/pos/guests/search", { params: { query } }),
    );
  }

  async getPosFolios(reservationId: string): Promise<ApiResult<PmsFolioDto[]>> {
    return this.handleRequest<PmsFolioDto[]>(
      this.client.get("/api/admin/pms/pos/folios", { params: { reservationId } }),
    );
  }

  async getPosFolio(folioId: string): Promise<ApiResult<PmsFolioDto>> {
    return this.handleRequest<PmsFolioDto>(
      this.client.get(`/api/admin/pms/pos/folios/${folioId}`),
    );
  }

  async ensureExternalFolio(payload: {
    outletKey: string;
    businessDate?: string;
    currency?: string;
    name?: string;
  }): Promise<ApiResult<PmsFolioDto>> {
    return this.handleRequest<PmsFolioDto>(
      this.client.post("/api/admin/pms/pos/external-folios", payload),
    );
  }

  async listPosPaymentMethods(): Promise<ApiResult<PmsPaymentMethodOptionDto[]>> {
    return this.handleRequest<PmsPaymentMethodOptionDto[]>(
      this.client.get("/api/admin/pms/pos/payment-methods"),
    );
  }

  async getPosPaymentMappings(): Promise<ApiResult<PmsPaymentMethodMappingDto[]>> {
    return this.handleRequest<PmsPaymentMethodMappingDto[]>(
      this.client.get("/api/admin/pms/pos/payment-mappings"),
    );
  }

  async savePosPaymentMappings(
    payload: PmsPaymentMethodMappingDto[],
  ): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.put("/api/admin/pms/pos/payment-mappings", payload),
    );
  }

  async listPosHistory(take = 100): Promise<ApiResult<PmsFolioOperationRecordDto[]>> {
    return this.handleRequest<PmsFolioOperationRecordDto[]>(
      this.client.get("/api/admin/pms/pos/history", { params: { take } }),
    );
  }

  async postPosCharge(payload: {
    reservationId?: string;
    folioId?: string;
    folioTargetType?: import("../types/pmsPosting.types").PmsFolioTargetType;
    folioCreationPolicy?: import("../types/pmsPosting.types").PmsFolioCreationPolicy;
    chargeKind?: import("../types/pmsPosting.types").PmsChargeKind;
    outletKey?: string;
    businessDate?: string;
    name: string;
    serviceType: string;
    vatType?: string;
    subAccountId?: string;
    amount: number;
    currency: string;
    receipt: string;
  }): Promise<ApiResult<PmsPosOperationResponseDto>> {
    return this.handleRequest<PmsPosOperationResponseDto>(
      this.client.post("/api/admin/pms/pos/charges", payload),
    );
  }

  async postPosPayment(payload: {
    reservationId?: string;
    folioId?: string;
    folioTargetType?: import("../types/pmsPosting.types").PmsFolioTargetType;
    folioCreationPolicy?: import("../types/pmsPosting.types").PmsFolioCreationPolicy;
    outletKey?: string;
    businessDate?: string;
    paymentMethod: string;
    paymentType?: string;
    paymentSubType?: string;
    amount: number;
    currency: string;
    receipt: string;
  }): Promise<ApiResult<PmsPosOperationResponseDto>> {
    return this.handleRequest<PmsPosOperationResponseDto>(
      this.client.post("/api/admin/pms/pos/payments", payload),
    );
  }

  async refundPosPayment(payload: {
    folioId: string;
    paymentId: string;
    amount: number;
    currency: string;
    receipt?: string;
    reason?: string;
    businessDate?: string;
  }): Promise<ApiResult<PmsPosOperationResponseDto>> {
    return this.handleRequest<PmsPosOperationResponseDto>(
      this.client.post("/api/admin/pms/pos/refunds", payload),
    );
  }

  async closePosFolio(folioId: string): Promise<ApiResult<PmsPosOperationResponseDto>> {
    return this.handleRequest<PmsPosOperationResponseDto>(
      this.client.post(`/api/admin/pms/pos/folios/${folioId}/close`, {}),
    );
  }

}
